import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useMemberProducts } from "@nju/data-access-portal-product";
import { FAQButton } from "@nju/feature-portal-help";
import { ErrorMessage, Radio, RadioGroup } from "@nju/form";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { A, Back, Button, Panel, Separator, Text } from "@nju/ui";
import { formatPhoneNumber, POSITIVE_DIGITS } from "@nju/utils";
import { selectCardTypeMachine } from "./SelectCardType.machine";
import { ISelectedSimCardType } from "./types";

export function SelectCardTypePage() {
  const router = useRouter();
  const params = portalRoutes.mojeKonto.mojaKarta.wymianaKarty.index
    .input(router.query)
    .unwrap();
  const { mobileProducts } = useMemberProducts();
  const clientNumber = mobileProducts.mobileAccess?.characteristic?.msisdn;

  const [{ context, matches }, send] = useMachine(selectCardTypeMachine, {
    actions: {
      redirectToChangeSimNewSimPage: () => {
        router.push(
          portalRoutes.mojeKonto.mojaKarta.wymianaKarty.sim.adresDostawy.get({
            ...(params?.userId && { userId: params.userId }),
          }),
        );
      },
      redirectToChangeSimEsimPage: () => {
        router.push(
          portalRoutes.mojeKonto.mojaKarta.wymianaKarty.esim.potwierdzDane.get({
            ...(params?.userId && { userId: params.userId }),
          }),
        );
      },
    },
  });

  const { errorMessage } = context;

  const onCardTypeChange = React.useCallback(
    (value: ISelectedSimCardType) => {
      send({ type: "SET_SIM_CARD_TYPE", value });
    },
    [send],
  );

  const handleNextStep = React.useCallback(() => {
    send({ type: "SUBMIT" });
  }, [send]);

  return (
    <Panel testId="sim-or-esim-selecting-panel">
      <Back
        iconColor="accent"
        noPadding={true}
        text="wstecz"
        textTone="accent"
        type="LINK"
        url={portalRoutes.mojeKonto.mojaKarta.index.get({
          ...(params?.userId && { userId: params.userId }),
        })}
      />
      <Separator size="xs" tone="invisible" />
      <Text
        size={{ "@onlyMobile": "keyS", "@fromTablet": "keyM" }}
        weight="bold"
      >
        karta SIM, czy eSIM?
      </Text>
      <Separator size="xs" tone="invisible" />
      <Text size={{ "@onlyMobile": "s", "@fromTablet": "m" }}>
        Możesz bezpłatnie wymienić kartę SIM na nową, zmienić fizyczną kartę na
        eSIM-a lub odwrotnie. Wymiana karty nie oznacza jej zablokowania - Twoje
        usługi pozostaną aktywne przez cały czas. W czasie wymiany twój PESEL
        nie może być zastrzeżony.
      </Text>
      <Separator size="xs" tone="invisible" />
      <Panel>
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center rounded-full	w-[30px] h-[30px] bg-cerulean">
            <Icon height={17} name="sim-card" tone="secondary" width={17} />
          </div>
          <Text
            size={{
              "@onlyMobile": "m",
              "@fromTablet": "l",
            }}
            weight="bold"
          >
            {clientNumber &&
              formatPhoneNumber(clientNumber, POSITIVE_DIGITS, " ", "", true)}
          </Text>
        </div>
        <Separator size="m" />
        <RadioGroup
          aria-label="sim card type"
          data-cy="document-type-radio"
          name="sim-card-type"
          onValueChange={(value) => {
            onCardTypeChange(value as ISelectedSimCardType);
          }}
        >
          <Radio space="narrow" value="eSIM">
            wybieram kartę eSIM
          </Radio>
          <Radio space="narrow" value="SIM">
            zamawiam nową kartę SIM
          </Radio>
        </RadioGroup>
      </Panel>
      <Separator size="s" tone="invisible" />
      <div className="flex gap-2">
        <Icon height={20} name="info-circle" tone="accent" width={20} />
        <Text size={{ "@onlyMobile": "s", "@fromTablet": "m" }}>
          Nie każde urządzenie obsługuje eSIM.
        </Text>
      </div>
      <Separator size="s" tone="invisible" />
      <FAQButton
        categoryId="NJU_WWW_ESIM_DEVICES"
        title="sprawdź listę urządzeń dla eSIM"
      />
      <Separator size="xs" tone="invisible" />
      {errorMessage && <ErrorMessage message={errorMessage} />}
      <Separator size="xs" tone="invisible" />
      <div className="flex justify-between only-mobile:flex-col-reverse only-mobile:gap-3">
        <Link
          href={portalRoutes.mojeKonto.mojaKarta.index.get({
            ...(params?.userId && { userId: params.userId }),
          })}
          passHref={true}
        >
          <A
            size={{
              "@onlyMobile": "s",
            }}
            variant="secondary"
          >
            anuluj
          </A>
        </Link>
        <Button
          isLoading={matches("goToNextStep")}
          onPress={handleNextStep}
          size={{
            "@onlyMobile": "s",
          }}
          type="submit"
        >
          dalej
        </Button>
      </div>
    </Panel>
  );
}
